/**
 * Mappers for reconciling UI types with Database types
 *
 * These functions transform between:
 * - BuildReviewEventRow (database schema - snake_case)
 * - BuildReviewEvent    (UI schema - camelCase)
 */

import type { BuildReviewEvent, BuildReviewAction, RevisionReason } from "@/data/buildReviewTypes";
import type { BuildReviewEventRow } from "@/services/build-review.service";

// ── DB → UI ───────────────────────────────────────────────────────────────

export function toUiBuildReviewEvent(db: BuildReviewEventRow): BuildReviewEvent {
  return {
    id: db.id,
    // Canonical join key in UI is jobs.id (UUID), not job_number.
    // Keep fallback for legacy rows if job_id is absent.
    jobId: db.job_id ?? db.job_number ?? "",
    action: db.action,
    timestamp: db.timestamp,
    performedBy: db.performed_by,
    notes: db.notes ?? undefined,
    revisionNotes: db.revision_notes ?? undefined,
    revisionReason: db.revision_reason ?? undefined,
    materialChanges: db.material_changes ?? undefined,
  };
}

// ── UI → DB (create) ──────────────────────────────────────────────────────
// Note: job_id (UUID) must be resolved from job_number before inserting.
// Pass the resolved UUID and job_number as arguments.

export function toDbBuildReviewEventCreate(
  ui: {
    action: BuildReviewAction;
    performedBy: string;
    notes?: string;
    revisionNotes?: string;
    revisionReason?: RevisionReason;
    materialChanges?: string;
  },
  jobUuid: string,
  jobNumber: string
): Omit<BuildReviewEventRow, "id" | "created_at"> {
  return {
    job_id: jobUuid,
    job_number: jobNumber,
    action: ui.action,
    timestamp: new Date().toISOString(),
    performed_by: ui.performedBy,
    notes: ui.notes ?? null,
    revision_notes: ui.revisionNotes ?? null,
    revision_reason: ui.revisionReason ?? null,
    material_changes: ui.materialChanges ?? null,
  };
}

// ── UI → DB (update) ──────────────────────────────────────────────────────
// Build review events are append-only logs — updates are rare.
// Only the mutable display fields are patchable.

export function toDbBuildReviewEventUpdate(
  ui: Partial<BuildReviewEvent>
): Partial<Omit<BuildReviewEventRow, "id" | "created_at">> {
  const patch: Partial<Omit<BuildReviewEventRow, "id" | "created_at">> = {};

  if (ui.action !== undefined) patch.action = ui.action;
  if (ui.timestamp !== undefined) patch.timestamp = ui.timestamp;
  if (ui.performedBy !== undefined) patch.performed_by = ui.performedBy;
  if (ui.notes !== undefined) patch.notes = ui.notes ?? null;
  if (ui.revisionNotes !== undefined) patch.revision_notes = ui.revisionNotes ?? null;
  if (ui.revisionReason !== undefined) patch.revision_reason = ui.revisionReason ?? null;
  if (ui.materialChanges !== undefined) patch.material_changes = ui.materialChanges ?? null;

  return patch;
}

// ── Batch ─────────────────────────────────────────────────────────────────

export function toUiBuildReviewEvents(rows: BuildReviewEventRow[]): BuildReviewEvent[] {
  return rows.map(toUiBuildReviewEvent);
}
